import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Box, Button, Drawer, Group, Loader, Stack, Text } from '@mantine/core';
import { MessagesSquare, Repeat, TrendingDown } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { SurfaceCard } from '@/design/SurfaceCard';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import type { StatItem } from '@/design/StatBand';
import type { AnalyticsFilter, AnomalySeverity, RePromptTurn } from '@/shared/analytics/AnalyticsTypes';
import { formatCount, formatTimestamp, formatTokens, humanUser } from '@/shared/analytics/MetricFormatting';
import { SEVERITY_COLORS } from '@/shared/signals/SignalSeverityColors';
import type { AnalyticsSurface } from './AnalyticsSurface';
import type { TopicDefinition } from './TopicCatalogTypes';
import PageLevelFilterRow from './PageLevelFilterRow';
import SurfaceBreadcrumbRow from './SurfaceBreadcrumbRow';
import SideRailPageLayout from './SideRailPageLayout';
import TopicSummaryPanel from './TopicSummaryPanel';
import ConversationViewer from './ConversationViewer';
import { SurfaceAssistToolbar } from './assist/SurfaceAssistScope';
import { useAssistFilters } from './assist/AssistSurfaceProvider';
import { RePromptTurnsViewModel } from './RePromptTurnsViewModel';

interface FrustrationPromptsBodyProps {
    surface: AnalyticsSurface;
    definition: TopicDefinition;
    /** The clicked person's user id. */
    primaryKey: string;
}

/** One repeated-ask cluster: the same prompt re-submitted N times within a span. */
interface Cluster {
    hash: string;
    prompt: string;
    count: number;
    tokens: number;
    spanMinutes: number;
    firstTimestamp: string;
    models: string[];
    severity: AnomalySeverity;
    /** Most recent turn in the cluster — the conversation to open. */
    sessionId: string;
    turnIndex: number;
}

const SEVERITY_LABEL: Record<AnomalySeverity, string> = { high: 'High', medium: 'Med', low: 'Low' };

/** More repeats of the same ask reads as more frustration. */
function severityForCount(count: number): AnomalySeverity {
    if (count >= 4) return 'high';
    if (count === 3) return 'medium';
    return 'low';
}

function formatSpan(minutes: number): string {
    if (minutes < 1) return 'under a min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    return `${(minutes / 60).toFixed(1)} hr`;
}

function buildClusters(turns: RePromptTurn[]): Cluster[] {
    const byHash = new Map<string, RePromptTurn[]>();
    for (const turn of turns) {
        const bucket = byHash.get(turn.promptHash) ?? [];
        bucket.push(turn);
        byHash.set(turn.promptHash, bucket);
    }
    const clusters: Cluster[] = [];
    for (const [hash, group] of byHash) {
        const ordered = [...group].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
        const first = ordered[0];
        const last = ordered[ordered.length - 1];
        if (first === undefined || last === undefined) continue;
        const spanMs = new Date(last.timestamp).getTime() - new Date(first.timestamp).getTime();
        clusters.push({
            hash,
            prompt: ordered.find((turn) => turn.promptText !== null && turn.promptText.length > 0)?.promptText ?? '(prompt text unavailable)',
            count: ordered.length,
            tokens: ordered.reduce((sum, turn) => sum + turn.tokens, 0),
            spanMinutes: Number.isFinite(spanMs) ? spanMs / 60000 : 0,
            firstTimestamp: first.timestamp,
            models: [...new Set(ordered.map((turn) => turn.model).filter((model) => model.length > 0))],
            severity: severityForCount(ordered.length),
            sessionId: last.sessionId,
            turnIndex: last.turnIndex,
        });
    }
    return clusters.sort((a, b) => b.count - a.count || b.tokens - a.tokens);
}

function SeverityPill({ severity }: { severity: AnomalySeverity }): ReactElement {
    const color = SEVERITY_COLORS[severity];
    return (
        <Box
            component="span"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                padding: '1px 7px',
                borderRadius: 999,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                color,
                background: `color-mix(in srgb, ${color} 14%, transparent)`,
                border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
            }}
        >
            {SEVERITY_LABEL[severity]}
        </Box>
    );
}

/**
 * The Frustration drill: a person's repeated prompts grouped into "asked N× in
 * M min" clusters, each showing the prompt text and opening the full conversation.
 * The clusters come from the re-prompt (restart) turns for this user.
 */
export default function FrustrationPromptsBody({ surface, definition, primaryKey }: FrustrationPromptsBodyProps): ReactElement {
    const vm = useViewModel(RePromptTurnsViewModel);
    const ambientFilters = useAssistFilters();
    const filters = useMemo<AnalyticsFilter[]>(() => [...ambientFilters], [ambientFilters]);
    const [open, setOpen] = useState<{ sessionId: string; turnIndex: number } | null>(null);

    useEffect(() => {
        void vm.load(primaryKey, filters);
    }, [vm, primaryKey, filters]);

    const clusters = useMemo(() => buildClusters(vm.turns), [vm.turns]);
    const personLabel = humanUser(primaryKey);
    const basePath = surface.basePath;

    const totalReprompts = clusters.reduce((sum, cluster) => sum + cluster.count, 0);
    const totalTokens = clusters.reduce((sum, cluster) => sum + cluster.tokens, 0);
    const stats: StatItem[] = [
        { label: 'Repeated asks', value: formatCount(clusters.length), hint: 'distinct prompts re-submitted' },
        { label: 'Re-prompts', value: formatCount(totalReprompts) },
        { label: 'Wasted tokens', value: formatTokens(totalTokens), hint: 'burned re-asking' },
    ];

    const rail = (
        <Stack gap="md">
            <TopicSummaryPanel stats={stats} />
        </Stack>
    );

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <SurfaceBreadcrumbRow
                items={[
                    { label: definition.navLabel ?? definition.title, to: `${basePath}/${definition.id}` },
                    { label: personLabel },
                ]}
            />

            <PageHeaderComponent
                icon={TrendingDown}
                eyebrow="Frustration · person"
                title={personLabel}
                subtitle="The prompts this person re-submitted near-identically — a proxy for answers that missed. Each cluster opens the conversation behind it."
            />

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            {vm.loading && clusters.length === 0 ?
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        Gathering re-prompts…
                    </Text>
                </Group>
            : clusters.length === 0 ?
                <Text size="sm" c="dimmed">
                    No re-prompts recorded for {personLabel} in this window.
                </Text>
            :   <SideRailPageLayout rail={rail}>
                    <Stack gap="sm">
                        {clusters.map((cluster) => (
                            <SurfaceCard key={cluster.hash} $padding="md">
                                <Group justify="space-between" align="center" wrap="nowrap" mb={8} gap="sm">
                                    <Group gap={8} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
                                        <Repeat size={15} color="var(--bm-color-brand-accent)" style={{ flexShrink: 0 }} />
                                        <Text fw={700} size="sm" style={{ whiteSpace: 'nowrap' }}>
                                            {cluster.count}× in {formatSpan(cluster.spanMinutes)}
                                        </Text>
                                        <SeverityPill severity={cluster.severity} />
                                    </Group>
                                    <Text size="xs" c="dimmed" ff="monospace" style={{ flexShrink: 0 }}>
                                        {formatTokens(cluster.tokens)} tok
                                    </Text>
                                </Group>

                                <Text size="sm" c="dimmed" lineClamp={3} style={{ lineHeight: 1.5 }}>
                                    “{cluster.prompt}”
                                </Text>

                                <Group justify="space-between" align="center" wrap="nowrap" mt={10} gap="sm">
                                    <Text size="xs" c="dimmed" truncate>
                                        {cluster.models.join(', ') || '—'} · first {formatTimestamp(cluster.firstTimestamp)}
                                    </Text>
                                    <Button
                                        size="compact-xs"
                                        variant="subtle"
                                        color="gray"
                                        leftSection={<MessagesSquare size={13} />}
                                        onClick={() => setOpen({ sessionId: cluster.sessionId, turnIndex: cluster.turnIndex })}
                                        style={{ flexShrink: 0 }}
                                    >
                                        Open conversation
                                    </Button>
                                </Group>
                            </SurfaceCard>
                        ))}
                    </Stack>
                </SideRailPageLayout>
            }

            <Drawer opened={open !== null} onClose={() => setOpen(null)} position="right" size="lg" title="Conversation" keepMounted={false}>
                {open !== null && <ConversationViewer sessionId={open.sessionId} focusTurnIndex={open.turnIndex} highlightAccent="rose" />}
            </Drawer>
        </Box>
    );
}
